import axios, { AxiosResponse } from "axios";
import {
  DashboardQuery,
  DashboardResponse,
  DashboardStats,
  PerformanceDataPoint,
  TransactionTypeData,
  UserFundSummary,
  TransactionSummary,
} from "./_models";
import { UserFundsQuery } from "../../check-user-funds/user-funds-list/core/_models";
import { TransactionHistoryQuery } from "../../transaction-history/transaction-history-list/core/_models";
import { createApiHeaders } from "../../../modules/auth/utils/api-headers";
const API_URL_DISBURSE = process.env.REACT_APP_DISBURSEMENT_API_URL;
const X_API_KEY = process.env.REACT_APP_DISBURSEMENT_X_API_KEY || "";

// Get User Funds for Dashboard
const getUserFundsForDashboard = (query: UserFundsQuery = {}): Promise<any> => {
  return axios
    .post(
      `${API_URL_DISBURSE}/merchant/account-balance/getdata`,
      {
        ...query,
        paging: {
          page: 1,
          limit: 10, // Only get recent data for dashboard
          sort: "receiveDate:-1",
        },
      },
      {
        headers: {
          ...createApiHeaders(),
        },
        withCredentials: true,
      }
    )
    .then((response: AxiosResponse<any>) => response.data);
};

// Get Transaction History for Dashboard
const getTransactionHistoryForDashboard = (
  query: Partial<TransactionHistoryQuery & { merchantId?: string }> = {}
): Promise<any> => {
  const today = new Date();
  const oneMonthAgo = new Date(
    today.getFullYear(),
    today.getMonth() - 1,
    today.getDate()
  );

  return axios
    .post(
      `${API_URL_DISBURSE}/merchant/report/transaction-history`,
      {
        fromDateTime: query.fromDateTime || oneMonthAgo.toISOString(),
        toDateTime: query.toDateTime || today.toISOString(),
        reportType: query.reportType || "ALL",
        send: false,
        type: query.type || "ALL",
        paging: {
          page: 1,
          limit: 1000, // Get more data for analytics
          sort: "createdAt:-1",
        },
      },
      {
        headers: {
          ...createApiHeaders(),
        },
        withCredentials: true,
      }
    )
    .then((response: AxiosResponse<any>) => response.data);
};

// Process dashboard data from raw API responses
const processDashboardData = (
  userFundsData: any,
  transactionData: any
): DashboardStats => {
  const userFunds = userFundsData?.detailData?.docs || [];
  const transactions = transactionData?.detailData?.docs || [];

  // Process User Funds Data
  const latestUserFund = userFunds[0];
  const totalBalance = latestUserFund?.amount || 0;
  const availableFunds = latestUserFund?.availableFund || 0;
  const holdAmount = latestUserFund?.holdAmount || 0;
  const holdAmountProvider = latestUserFund?.holdAmountProvider || 0;

  // Process Transaction Data
  const today = new Date();
  const todayStr = today.toISOString().split("T")[0];
  const weekAgo = new Date(today.getTime() - 7 * 24 * 60 * 60 * 1000);
  const monthAgo = new Date(
    today.getFullYear(),
    today.getMonth() - 1,
    today.getDate()
  );

  // Filter transactions by date
  const todayTransactions = transactions.filter((t: any) =>
    t.createdAt?.startsWith(todayStr)
  );

  const weeklyTransactions = transactions.filter(
    (t: any) => new Date(t.createdAt) >= weekAgo
  );

  const monthlyTransactions = transactions.filter(
    (t: any) => new Date(t.createdAt) >= monthAgo
  );

  // Calculate transaction stats
  const successfulTransactions = transactions.filter(
    (t: any) => t.status === "SUCCESS" || t.operationStatus === "SUCCESS"
  ).length;

  const failedTransactions = transactions.filter(
    (t: any) => t.status === "FAILED" || t.operationStatus === "FAILED"
  ).length;

  const pendingTransactions = transactions.filter(
    (t: any) => t.status === "PENDING" || t.operationStatus === "PENDING"
  ).length;

  // Calculate amounts
  const todayTransactionAmount = todayTransactions.reduce(
    (sum: number, t: any) => sum + (t.totalAmount || t.transferAmount || 0),
    0
  );

  const weeklyTransactionAmount = weeklyTransactions.reduce(
    (sum: number, t: any) => sum + (t.totalAmount || t.transferAmount || 0),
    0
  );

  const monthlyTransactionAmount = monthlyTransactions.reduce(
    (sum: number, t: any) => sum + (t.totalAmount || t.transferAmount || 0),
    0
  );

  // Group by transaction type
  const transferTransactions = transactions.filter(
    (t: any) => t.type === "TRANSFER" || t.type === "transfer"
  ).length;

  const disbursementTransactions = transactions.filter(
    (t: any) => t.type === "DISBURSEMENT" || t.type === "disbursement"
  ).length;

  const topupTransactions = transactions.filter(
    (t: any) => t.type === "TOPUP" || t.type === "topup"
  ).length;

  // Generate performance data for chart (last 30 days)
  const performanceData: PerformanceDataPoint[] = [];
  for (let i = 29; i >= 0; i--) {
    const date = new Date(today.getTime() - i * 24 * 60 * 60 * 1000);
    const dateStr = date.toISOString().split("T")[0];

    const dayTransactions = transactions.filter((t: any) =>
      t.createdAt?.startsWith(dateStr)
    );

    const dayAmount = dayTransactions.reduce(
      (sum: number, t: any) => sum + (t.totalAmount || t.transferAmount || 0),
      0
    );

    performanceData.push({
      date: dateStr,
      amount: dayAmount,
      transactions: dayTransactions.length,
    });
  }

  // Generate transaction type data for donut chart
  const transactionTypeData: TransactionTypeData[] = [
    {
      type: "Transfer",
      count: transferTransactions,
      amount: transactions
        .filter((t: any) => t.type === "TRANSFER" || t.type === "transfer")
        .reduce(
          (sum: number, t: any) =>
            sum + (t.totalAmount || t.transferAmount || 0),
          0
        ),
      percentage: Math.round(
        (transferTransactions / Math.max(transactions.length, 1)) * 100
      ),
      color: "#ff6b35",
    },
    {
      type: "Disbursement",
      count: disbursementTransactions,
      amount: transactions
        .filter(
          (t: any) => t.type === "DISBURSEMENT" || t.type === "disbursement"
        )
        .reduce(
          (sum: number, t: any) =>
            sum + (t.totalAmount || t.transferAmount || 0),
          0
        ),
      percentage: Math.round(
        (disbursementTransactions / Math.max(transactions.length, 1)) * 100
      ),
      color: "#50cd89",
    },
    {
      type: "Top Up",
      count: topupTransactions,
      amount: transactions
        .filter((t: any) => t.type === "TOPUP" || t.type === "topup")
        .reduce(
          (sum: number, t: any) =>
            sum + (t.totalAmount || t.transferAmount || 0),
          0
        ),
      percentage: Math.round(
        (topupTransactions / Math.max(transactions.length, 1)) * 100
      ),
      color: "#377dff",
    },
  ];

  return {
    totalBalance,
    availableFunds,
    holdAmount,
    holdAmountProvider,
    todayTransactions: todayTransactions.length,
    weeklyTransactions: weeklyTransactions.length,
    monthlyTransactions: monthlyTransactions.length,
    totalTransactions: transactions.length,
    todayTransactionAmount,
    weeklyTransactionAmount,
    monthlyTransactionAmount,
    successfulTransactions,
    failedTransactions,
    pendingTransactions,
    transferTransactions,
    disbursementTransactions,
    topupTransactions,
    performanceData,
    transactionTypeData,
  };
};

// Main dashboard data fetcher
const getDashboardData = async (
  query: DashboardQuery = {}
): Promise<DashboardResponse> => {
  try {
    // Fetch both user funds and transaction history in parallel
    const [userFundsResponse, transactionResponse] = await Promise.all([
      getUserFundsForDashboard({
        searchData: query.merchantId,
      }),
      getTransactionHistoryForDashboard({
        fromDateTime: query.startDate
          ? new Date(query.startDate).toISOString()
          : undefined,
        toDateTime: query.endDate
          ? new Date(query.endDate).toISOString()
          : undefined,
        merchantId: query.merchantId,
      }),
    ]);

    // Process the raw data
    const stats = processDashboardData(userFundsResponse, transactionResponse);

    // Extract user fund summary
    const latestUserFund = userFundsResponse?.detailData?.docs?.[0];
    const userFundSummary: UserFundSummary = {
      merchantId: latestUserFund?.merchantId || "",
      merchantName: latestUserFund?.merchantName || "",
      totalBalance: stats.totalBalance,
      availableFunds: stats.availableFunds,
      holdAmount: stats.holdAmount,
      holdAmountProvider: stats.holdAmountProvider,
      lastUpdated: latestUserFund?.receiveDate || new Date().toISOString(),
    };

    // Extract transaction summary
    const transactionSummary: TransactionSummary = {
      totalTransactions: stats.totalTransactions,
      successfulTransactions: stats.successfulTransactions,
      failedTransactions: stats.failedTransactions,
      pendingTransactions: stats.pendingTransactions,
      totalAmount: stats.monthlyTransactionAmount,
      averageAmount:
        stats.totalTransactions > 0
          ? stats.monthlyTransactionAmount / stats.totalTransactions
          : 0,
      period: query.period || "month",
    };

    return {
      userFundSummary,
      transactionSummary,
      performanceData: stats.performanceData,
      transactionTypeData: stats.transactionTypeData,
      stats,
      success: true,
    };
  } catch (error: any) {
    console.error("Dashboard data fetch error:", error);

    // Return default/fallback data
    return {
      userFundSummary: {
        merchantId: "",
        merchantName: "",
        totalBalance: 0,
        availableFunds: 0,
        holdAmount: 0,
        holdAmountProvider: 0,
        lastUpdated: new Date().toISOString(),
      },
      transactionSummary: {
        totalTransactions: 0,
        successfulTransactions: 0,
        failedTransactions: 0,
        pendingTransactions: 0,
        totalAmount: 0,
        averageAmount: 0,
        period: "month",
      },
      performanceData: [],
      transactionTypeData: [],
      stats: {
        totalBalance: 0,
        availableFunds: 0,
        holdAmount: 0,
        holdAmountProvider: 0,
        todayTransactions: 0,
        weeklyTransactions: 0,
        monthlyTransactions: 0,
        totalTransactions: 0,
        todayTransactionAmount: 0,
        weeklyTransactionAmount: 0,
        monthlyTransactionAmount: 0,
        successfulTransactions: 0,
        failedTransactions: 0,
        pendingTransactions: 0,
        transferTransactions: 0,
        disbursementTransactions: 0,
        topupTransactions: 0,
        performanceData: [],
        transactionTypeData: [],
      },
      success: false,
      message: error.message || "Failed to fetch dashboard data",
    };
  }
};

export {
  getDashboardData,
  getUserFundsForDashboard,
  getTransactionHistoryForDashboard,
  processDashboardData,
};
